import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { ConversationsLayout } from './pages/ConversationsLayout';
import { BulkDispatchesPage } from './pages/BulkDispatchesPage';
import FlowsPage from './pages/FlowsPage';
import { Departments } from './pages/Departments';
import { AdminSettings } from './pages/AdminSettings';
import { Users } from './pages/Users';
import { UserLoginReport } from './pages/UserLoginReport';
import { InternalChat } from './pages/InternalChat';
import { Ratings } from './pages/Ratings';
import { Contacts } from './pages/Contacts';
import { WebhookConfig } from './pages/WebhookConfig';
import { UserProfile } from './pages/UserProfile';
import SatisfactionStats from './pages/SatisfactionStats';

function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <Login />}
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="conversations" element={<ConversationsLayout />} />
        <Route path="conversations/:conversationId" element={<ConversationsLayout />} />
        <Route
          path="bulk-dispatches"
          element={
            <ProtectedRoute requireAdmin>
              <BulkDispatchesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="flows"
          element={
            <ProtectedRoute requireAdmin>
              <FlowsPage />
            </ProtectedRoute>
          }
        />
        <Route path="contacts" element={<Contacts />} />
        <Route
          path="ratings"
          element={
            <ProtectedRoute requireAdminOrManager>
              <Ratings />
            </ProtectedRoute>
          }
        />
        <Route path="internal-chat" element={<InternalChat />} />
        <Route path="profile" element={<UserProfile />} />
        <Route path="users" element={<Users />} />
        <Route
          path="user-login-report"
          element={
            <ProtectedRoute requireAdminOrGeneralManager>
              <UserLoginReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="departments"
          element={
            <ProtectedRoute requireAdminOrGeneralManager>
              <Departments />
            </ProtectedRoute>
          }
        />
        <Route
          path="satisfaction-stats"
          element={
            <ProtectedRoute requireAdminOrManager>
              <SatisfactionStats />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin-settings"
          element={
            <ProtectedRoute requireAdmin>
              <AdminSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="webhook-config"
          element={
            <ProtectedRoute requireAdmin>
              <WebhookConfig />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <SettingsProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </SettingsProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
